import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { siteConfig } from '../data/mock';
import { 
  Leaf, 
  TreePine, 
  Bird, 
  Flower2, 
  Sparkles,
  BookOpen,
  ArrowRight
} from 'lucide-react';

const LoginPage = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50 relative overflow-hidden">
      {/* Animated nature background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating leaves */}
        <div className="absolute top-20 left-10 animate-float">
          <Leaf className="w-8 h-8 text-emerald-400 opacity-60" />
        </div>
        <div className="absolute top-40 right-20 animate-float" style={{ animationDelay: '1s' }}>
          <Leaf className="w-6 h-6 text-green-500 opacity-50 rotate-45" />
        </div>
        <div className="absolute bottom-40 left-20 animate-float" style={{ animationDelay: '2s' }}>
          <Flower2 className="w-10 h-10 text-coral-400 opacity-50" />
        </div>
        <div className="absolute top-60 left-1/4 animate-float" style={{ animationDelay: '0.5s' }}>
          <Bird className="w-8 h-8 text-sky-500 opacity-40" />
        </div>
        <div className="absolute bottom-60 right-1/4 animate-float" style={{ animationDelay: '1.5s' }}>
          <TreePine className="w-12 h-12 text-emerald-600 opacity-30" />
        </div>
        
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-100/20 rounded-full blur-3xl" />
      </div>

      <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-white/50 rounded-full blur-xl" />
            <img
              src={siteConfig.logo}
              alt={siteConfig.name}
              className="relative w-32 h-32 object-contain"
            />
          </div>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-md">
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 lg:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-4">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Welcome!</span>
              </div>
              <h1 className="text-3xl font-serif font-bold text-slate-800 mb-2">
                Log in to Creassion Resources
              </h1>
              <p className="text-slate-600">
                Start your faith-building journey with amazing Bible stories
              </p>
            </div>

            {/* Google Sign In Button */}
            <Button
              onClick={login}
              className="w-full bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 rounded-2xl py-6 text-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 group"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
              <ArrowRight className="w-5 h-5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Button>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-slate-500">Safe & Secure</span>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              {[
                { icon: BookOpen, text: "Access amazing Bible stories" },
                { icon: Sparkles, text: "Interactive flipbook reading" },
                { icon: Leaf, text: "Track your reading journey" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-600">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Back to home */}
          <div className="text-center mt-6">
            <a
              href="/"
              className="text-slate-600 hover:text-coral-600 text-sm font-medium transition-colors"
            >
              ← Back to Home
            </a>
          </div>
        </div>

        {/* Fun characters at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none">
          <div className="absolute bottom-4 left-1/4 animate-bounce" style={{ animationDelay: '0.5s' }}>
            <div className="w-4 h-4 bg-coral-400 rounded-full" />
          </div>
          <div className="absolute bottom-8 left-1/3 animate-bounce" style={{ animationDelay: '1s' }}>
            <div className="w-3 h-3 bg-emerald-400 rounded-full" />
          </div>
          <div className="absolute bottom-4 right-1/4 animate-bounce" style={{ animationDelay: '1.5s' }}>
            <div className="w-5 h-5 bg-sky-400 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
